/**
 * @file: TimelineMessage.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * One row of the feed thread timeline. Plain layout — no card chrome.
 * The left rail (rail-line + small dot from `TimelineItem`) sits next
 * to inline content: a tiny header line (category chip + relative
 * time), then the type-specific rich body from `MessageBody`, then an
 * optional author chip and a thin separator below.
 *
 * `isFirst` / `isLast` control rail terminators at the list edges.
 */

import { Typography } from '@euler/app/components/typography';
import { Avatar } from '@euler/components/Avatar';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FeedInboxItem, FeedMessageAuthorType } from '@euler/model/feed';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { descriptorFor } from '../utils/feedMessageDescriptor';
import { relativeTime } from '../utils/relativeTime';
import { MessageBody } from './MessageBody';
import { TimelineItem } from './TimelineItem';

type Props = {
  message: FeedInboxItem;
  isFirst?: boolean;
  isLast?: boolean;
  /** Mark a single message read by recipient-row id (manual-read threads). */
  onMarkRead?: (recipientId: string) => void;
  /** Convert-to-lead bundle done — mark all bundled reminders read. */
  onConvertedToLead?: (planDetailIds: number[]) => void;
};

export const TimelineMessage: FC<Props> = memo(
  ({ message, isFirst, isLast, onMarkRead, onConvertedToLead }) => {
    const { rpx } = useResponsiveContext();
    const desc = descriptorFor(message.params);
    const unread = !message.readAt;
    const isHuman =
      message.author?.type === FeedMessageAuthorType.OrgMember ||
      message.author?.type === FeedMessageAuthorType.OrgUser;
    return (
      <TimelineItem
        accent={desc.accent}
        isFirst={isFirst}
        isLast={isLast}
        read={!unread}
      >
        <View
          css={`
            padding-top: ${rpx(2)}px;
          `}
        >
          <View
            css={`
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              margin-bottom: ${rpx(4)}px;
            `}
          >
            <Typography.Text size={rpx(11)} color={desc.accent} medium>
              {desc.category}
            </Typography.Text>
            <Typography.Text size={rpx(11)} color="#9ca3af">
              {relativeTime(message.createdAt)}
            </Typography.Text>
          </View>

          <MessageBody
            payload={message.params}
            author={message.author}
            messageId={message.id}
            readAt={message.readAt}
            onMarkRead={onMarkRead}
            onConvertedToLead={onConvertedToLead}
          />

          {isHuman && message.author?.name ? (
            <View
              css={`
                flex-direction: row;
                align-items: center;
                margin-top: ${rpx(8)}px;
              `}
            >
              <Avatar
                uri={message.author.avatar ?? undefined}
                name={message.author.name ?? '?'}
                css={`
                  width: ${rpx(18)}px;
                  height: ${rpx(18)}px;
                  border-radius: ${rpx(9)}px;
                `}
              />
              <Typography.Text ml={rpx(6)} size={rpx(11)} color="#6b7280">
                {message.author.name}
              </Typography.Text>
            </View>
          ) : null}

          {isLast ? null : (
            <View
              style={{
                height: 1,
                backgroundColor: '#eef0f3',
                marginTop: rpx(14),
              }}
            />
          )}
        </View>
      </TimelineItem>
    );
  },
);
